import { useSyncExternalStore } from "react";
import { useTheme } from "@/lib/theme";

/**
 * Ícones de arquivo do tema "Seti" — o padrão do VS Code —, com as mesmas cores do VS Code
 * (variações para o tema escuro e o claro). Os dados (SVG do Seti-UI e o mapeamento por nome e
 * extensão) carregam à parte, na primeira vez que um ícone aparece; até lá, o ícone de antes.
 * Licença e origem: LICENSE.md nesta pasta.
 */

type Definitions = {
  files: Record<string, [string, string]>;
  extensions: Record<string, [string, string]>;
  partials: Array<[string, [string, string]]>;
  default: [string, string];
};

const DARK: Record<string, string> = {
  blue: "#519aba",
  grey: "#4d5a5e",
  "grey-light": "#6d8086",
  green: "#8dc149",
  orange: "#e37933",
  pink: "#f55385",
  purple: "#a074c4",
  red: "#cc3e44",
  white: "#d4d7d6",
  yellow: "#cbcb41",
  ignore: "#41535b",
};

const LIGHT: Record<string, string> = {
  blue: "#498ba7",
  grey: "#455155",
  "grey-light": "#627379",
  green: "#7fae42",
  orange: "#cc6d2e",
  pink: "#dd4b78",
  purple: "#9068b0",
  red: "#b8383d",
  white: "#bfc2c1",
  yellow: "#b7b73b",
  ignore: "#3b4b52",
};

/** Nomes que o VS Code mapeia além do Seti-UI (ex.: package.json com o ícone do npm). */
const EXTRA_FILES: Record<string, [string, string]> = {
  "package.json": ["npm", "red"],
  "package-lock.json": ["npm", "red"],
  ".npmrc": ["npm", "red"],
  "pnpm-lock.yaml": ["yarn", "orange"],
};

let data: { defs: Definitions; icons: Record<string, string> } | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function load() {
  loading ??= Promise.all([import("./definitions.json"), import("./icons.json")]).then(([d, i]) => {
    data = { defs: d.default as unknown as Definitions, icons: i.default as Record<string, string> };
    listeners.forEach((l) => l());
  });
  return loading;
}

function useSetiData() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      void load();
      return () => listeners.delete(l);
    },
    () => data,
    () => data,
  );
}

/** Ícone e cor do Seti para o nome do arquivo (a mesma ordem de busca do tema). */
export function setiFor(defs: Definitions, fileName: string): [string, string] {
  const name = fileName.split("/").pop() ?? fileName;
  if (EXTRA_FILES[name]) return EXTRA_FILES[name];
  if (Object.prototype.hasOwnProperty.call(defs.files, name)) return defs.files[name]!;
  let ext = name.slice(name.indexOf("."));
  while (name.includes(".") && ext !== "") {
    if (Object.prototype.hasOwnProperty.call(defs.extensions, ext)) return defs.extensions[ext]!;
    ext = ext.slice(1);
    ext = ext.includes(".") ? ext.slice(ext.indexOf(".")) : "";
  }
  for (const [partial, value] of defs.partials) if (name.includes(partial)) return value;
  return defs.default;
}

/** Ícone Seti do arquivo; `fallback` aparece enquanto os dados carregam. */
export function SetiIcon({
  name,
  className = "h-4 w-4",
  fallback = null,
}: {
  name: string;
  className?: string;
  fallback?: React.ReactNode;
}) {
  const seti = useSetiData();
  const dark = useTheme((s) => s.theme === "dark");
  if (!seti) return <>{fallback}</>;
  const [icon, color] = setiFor(seti.defs, name);
  const svg = seti.icons[icon] ?? seti.icons[seti.defs.default[0]];
  // Só SVG dos próprios dados do tema (nada vindo de fora).
  if (!svg || !svg.startsWith("<svg") || svg.includes("<script")) return <>{fallback}</>;
  return (
    <span
      aria-hidden
      className={`inline-flex shrink-0 items-center justify-center [&>svg]:h-full [&>svg]:w-full [&>svg]:fill-current ${className}`}
      style={{ color: (dark ? DARK : LIGHT)[color] ?? color }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
